import { onUnmounted, ref } from 'vue'
import { AppUtil } from './AppUtil'
import { BrowserUtil } from './BrowserUtil'

export class AudioPlayerMemento {
  constructor(private state: boolean) {}

  public getState() {
    return this.state
  }

  public setState(state: boolean) {
    this.state = state
  }
}

export class AudioPlayer {
  public audio: HTMLAudioElement

  playing = false

  constructor(public url: string, loop = false) {
    this.audio = document.createElement('audio')
    this.audio.loop = loop

    this.audio.addEventListener('ended', () => {
      this.playing = false
    })
    this.audio.addEventListener('error', () => {
      this.playing = false
    })

    this.audio.src = this.url
  }

  public async play() {
    try {
      return new Promise((_resolve, _reject) => {
        this.audio.addEventListener('ended', _resolve)
        this.audio.addEventListener('error', _reject)
        this.audio.play()
        this.playing = true
      })
    } catch {
      return
    }
  }

  // warning: not work
  public async ready() {
    this.audio.preload = 'auto'
    this.audio.load()

    return new Promise((_resolve, _reject) => {
      this.audio.addEventListener('load', _resolve)
      this.audio.addEventListener('error', _reject)
    })
  }

  public async pause() {
    try {
      await this.audio.pause()
      this.playing = false
    } catch {
      return
    }
  }

  public setSrc(src: string) {
    if (this.audio.src !== src) {
      this.pause()
      this.audio.src = src
      this.audio.load()
    }
  }

  public getSrc() {
    return this.audio.src
  }

  public getPlaying() {
    return this.playing
  }

  public setPlaying(playing: boolean) {
    if (playing !== this.playing) {
      if (playing) {
        return this.play()
      } else {
        return this.pause()
      }
    }
  }

  public createMemento() {
    return new AudioPlayerMemento(this.getPlaying())
  }

  public restoreMemento(memento: AudioPlayerMemento) {
    return this.setPlaying(memento.getState())
  }

  public static async play(url: string) {
    if (BrowserUtil.isUnderTzyApp()) {
      if (await AppUtil.isAppVersionHigherThan('4.0.42')) {
        return AppUtil.asyncPlayAudio(url)
      }
    }
    return new AudioPlayer(url).play()
  }
}

export function useAudioPlayer() {
  const audio = ref<HTMLAudioElement | null>(null)
  const isPlaying = ref(false)

  const play = (url: string): Promise<void> => {
    if (!url) {
      return Promise.resolve()
    }

    return new Promise((resolve, reject) => {
      stop()

      const audioElement = new Audio(url)
      audio.value = audioElement

      let settled = false

      const onEnded = () => {
        if (settled) return
        settled = true
        isPlaying.value = false
        cleanup()
        resolve()
      }

      const onError = (e: Event) => {
        if (settled) return
        settled = true
        isPlaying.value = false
        cleanup()
        reject(e)
      }

      const cleanup = () => {
        audioElement.removeEventListener('ended', onEnded)
        audioElement.removeEventListener('error', onError)
        if (audio.value === audioElement) {
          audio.value = null
        }
      }

      audioElement.addEventListener('ended', onEnded)
      audioElement.addEventListener('error', onError)

      const playPromise = audioElement.play()
      if (playPromise) {
        playPromise
          .then(() => {
            // play() 成功返回了，等待 ended 事件
            isPlaying.value = true
          })
          .catch((err) => {
            if (settled) return
            settled = true
            isPlaying.value = false
            cleanup()
            reject(err)
          })
      } else {
        // 某些浏览器可能返回 undefined，假设正在播放
        isPlaying.value = true
      }
    })
  }

  const stop = () => {
    if (audio.value) {
      audio.value.pause()
      audio.value.currentTime = 0
      audio.value = null
      isPlaying.value = false
    }
  }

  onUnmounted(() => {
    stop()
  })

  return {
    play,
    stop,
    isPlaying,
  }
}
